<!--

============================================================================================================
||    FlowTriggerFrequency page                                                                           ||
============================================================================================================

|   Description
------------------------------------------------------------------------------------------------------------
  Forth step in the flow creation process. Enables user to specify how frequently the flow will trigger.

|   Events - page should not emit events
------------------------------------------------------------------------------------------------------------
  - input       : Object    : Emits object with valid and selected parameters.

-->

<template>
  <div class="flow-trigger-frequency-step">
    <h2>Choose how frequently this AI flow will trigger</h2>
    <!-- <pre class="debug"> {{ selected }}</pre> -->
    <p>Define how often this flow will retrigger for the same contact.</p>
    <ValidationObserver ref="triggerObserver" >
      <ValidationProvider
        name="frequency"
        rules="required">
        <BlockSelect
          v-if="triggerOptions && triggerOptions.length"
          v-model="selected"
          :options="triggerOptions"
          :preselect-first="true" />
      </ValidationProvider>
    </ValidationObserver>
  </div>
</template>

<script>
import { duration } from 'moment';
import BlockSelect from '@/components/common/BlockSelect';
import BlockSelectTypes from '@/helpers/lists/BlockSelectTypes';
import { mapGetters } from 'vuex';

export default {
  name: 'FlowTriggerFrequencyStep',
  components: {
    BlockSelect,
  },
  data() {
    return {
      selected: {},
      triggerOptions: [],
    };
  },
  computed: {
    ...mapGetters({
      flowsDefinitions: 'flows/flowsDefinitions',
    }),
  },
  watch: {
    selected: {
      handler() {
        this.emit();
      },
      immediate: true,
    },
  },
  mounted() {
    this.initialTimeTitles();
  },
  methods: {
    initialTimeTitles() {
      this.triggerOptions.forEach((o) => {
        if (o.type === BlockSelectTypes.TIME) {
          o.title = o.titleTemplate.replace('{{TIME_PLACEHOLDER}}', o.value ? this.humanReadableTime(o.value) : this.humanReadableTime(o.minSeconds || 60) || '<span class="diminish">0m</span>');
        }
      });
    },
    setStage() {
      this.setOptions();
      this.initialTimeTitles();
    },
    triggerType(type) {
      switch (type) {
        case 'with_delay':
          return BlockSelectTypes.TIME;
        default:
          return BlockSelectTypes.OPTION;
      }
    },
    setOptions() {
      this.triggerOptions = [];
      this.flowsDefinitions.flows.params.trigger_frequency.forEach((trigger) => {
        const frequencyType = this.triggerType(trigger.type);
        this.triggerOptions.push({
          id: trigger.type,
          image: trigger.icon,
          title: frequencyType !== BlockSelectTypes.TIME ? trigger.description : null,
          titleTemplate: frequencyType === BlockSelectTypes.TIME ? trigger.description_template : null,
          type: frequencyType,
          value: 86400,
          minSeconds: trigger.minSeconds || 60,
          minSecondsMessage: trigger.minSecondsMessage || 'Retrigger period can\'t be less than 1 minute.',
        });
      });
    },
    humanReadableTime(seconds) {
      /* eslint-disable no-underscore-dangle */
      const parsedTime = duration(seconds, 'seconds');
      const days = Math.floor(parsedTime.asDays());
      const timeParts = [];
      if (days) {
        if (seconds === 86400) {
          timeParts.push('<span class="highlight">24h</span>');
        } else {
          timeParts.push(`<span class="highlight">${days}d</span>`);
        }
      }
      if (parsedTime._data.hours) {
        timeParts.push(`<span class="highlight">${parsedTime._data.hours}h</span>`);
      }

      if (parsedTime._data.minutes) {
        timeParts.push(`<span class="highlight">${parsedTime._data.minutes}m</span>`);
      }
      if (parsedTime._data.seconds) {
        timeParts.push(`<span class="highlight">${parsedTime._data.seconds}s</span>`);
      }
      /* eslint-enable no-underscore-dangle */

      const lastTimePart = timeParts.pop();
      let timeString = timeParts.join(', ');
      if (lastTimePart) timeString += timeParts.length ? ` and ${lastTimePart}` : lastTimePart;
      return timeString;
    },
    emit() {
      if (!this.$refs.triggerObserver) return;
      this.$refs.triggerObserver.validate()
        .then((valid) => {
          this.initialTimeTitles();
          this.selected.selected.forEach((s) => {
            const targetOption = this.triggerOptions.find(option => option.id === s.id);
            if (targetOption && targetOption.type === BlockSelectTypes.TIME) {
              targetOption.title = targetOption.titleTemplate.replace('{{TIME_PLACEHOLDER}}', s.additionalData && s.additionalData.seconds ? this.humanReadableTime(s.additionalData.seconds) : `<span class="diminish">0${s.additionalData && s.additionalData.timeScope ? s.additionalData.timeScope.name[0].toLowerCase() : 's'}</span>`);
            }
          });
          this.$emit('input', {
            valid: valid && this.selected.valid,
            name: 'flowTriggerFrequency',
            data: {
              flowTriggerFrequenciesDetails: this.selected,
            },
          });
        })
        .catch((error) => {
          // console.error('Error validating trigger frequency', error);
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.flow-trigger-frequency-step {
  h2, p {
    text-align: center;
  }

  h2 {
    font-size: 18px;
    font-weight: bold;
    color: $blackPrimary;
    margin-bottom: 5px;
  }

  p {
    font-size: 14px;
    font-weight: normal;
    color: $blackUltralight;
    margin-bottom: 33px;
  }
}
</style>
